import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import type { FilterValues, FilterChangeHandler, ActiveFilterPill } from './types';
import { allFilters, defaultFilterValues, getFilterById } from './filterConfig';
import { coerceFilterBoolean, isExplicitlyOn } from './filterBoolean';

// Format value for display in pills
const formatValueForDisplay = (filterId: string, value: any): string => {
  const filter = getFilterById(filterId);
  if (!filter) return String(value);

  // Handle boolean toggles
  if (filter.type === 'toggle' || (filter.type === 'toggle-with-detail' && filter.enabledKey === filterId)) {
    return isExplicitlyOn(value) ? 'On' : 'Off';
  }

  // Handle number inputs
  if (filter.type === 'number') {
    if (filter.unit === 'percent') {
      return `${(value * 100).toFixed(1)}%`;
    }
    if (filter.prefix === '$') {
      if (value >= 1000000000) {
        return `$${(value / 1000000000).toFixed(1)}B`;
      }
      if (value >= 1000000) {
        return `$${(value / 1000000).toFixed(1)}M`;
      }
      return `$${value.toLocaleString()}`;
    }
    if (filter.suffix === '%') {
      return `${(value * 100).toFixed(1)}%`;
    }
    if (filter.suffix === 'x') {
      return `${value.toFixed(1)}x`;
    }
    if (filter.suffix === ' mo') {
      return `${value} mo`;
    }
    return String(value);
  }

  // Handle select/radio
  if (filter.type === 'select' || filter.type === 'card-radio-group') {
    const option = filter.options?.find((o) => o.value === value);
    return option?.label || String(value);
  }

  // Handle checkbox groups
  if (filter.type === 'card-checkbox-group') {
    if (Array.isArray(value)) {
      if (value.length === 0) return 'None';
      if (value.length === filter.options?.length) return 'All';
      return value.length > 2 ? `${value.length} selected` : value.join(', ');
    }
    return String(value);
  }

  // Handle segmented
  if (filter.type === 'segmented') {
    return value;
  }

  return String(value);
};

interface UseFilterStateOptions {
  initialValues: FilterValues;
  /** When provided, footer Reset merges this baseline over catalog defaults (open snapshot). */
  getResetBaseline?: () => FilterValues;
}

export type { UseFilterStateOptions };

interface UseFilterStateReturn {
  // State
  filterValues: FilterValues;
  activeCategory: string;
  searchQuery: string;
  activePills: ActiveFilterPill[];
  filteredConfig: typeof allFilters;
  expandedDetails: Record<string, boolean>;

  // Actions
  setValue: FilterChangeHandler;
  setActiveCategory: (category: string) => void;
  setSearchQuery: (query: string) => void;
  resetFilter: (filterId: string) => void;
  resetAll: () => void;
  resetToDefaults: () => void;
  toggleDetail: (filterId: string) => void;
  setDetailExpanded: (filterId: string, open: boolean) => void;
}

export const useFilterState = ({ initialValues, getResetBaseline }: UseFilterStateOptions): UseFilterStateReturn => {
  // Initialize filter values with provided values or defaults
  const [filterValues, setFilterValues] = useState<FilterValues>(() => {
    return { ...defaultFilterValues, ...initialValues };
  });

  // Active category for sidebar
  const [activeCategory, setActiveCategory] = useState<string>('market');

  // Search query
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Expanded state for toggle-with-detail panels
  const [expandedDetails, setExpandedDetails] = useState<Record<string, boolean>>({});

  // Bug 9 fix: Track whether we've synced initial values to avoid overwriting user edits
  const hasSyncedInitial = useRef(false);

  // Update filter values when initialValues change - only sync once on first load
  useEffect(() => {
    if (!hasSyncedInitial.current) {
      setFilterValues({ ...defaultFilterValues, ...initialValues });
      hasSyncedInitial.current = true;
    }
    // Intentionally NOT re-syncing on subsequent initialValues changes
    // to avoid overwriting user edits in the dialog
  }, [initialValues]);

  // Calculate active pills - filters that deviate from defaults
  const activePills = useMemo((): ActiveFilterPill[] => {
    const pills: ActiveFilterPill[] = [];

    allFilters.forEach((filter) => {
      // Skip if this is an enabled key (the toggle itself)
      if (filter.enabledKey && filter.type === 'toggle-with-detail') {
        const enabledValue = filterValues[filter.enabledKey];
        const defOn = Boolean(filter.defaultValue);
        const on = coerceFilterBoolean(enabledValue, defOn);
        // Only show "active" when the feature is on; avoid confusing "Feature · Off"
        // for default-on filters (e.g. Strong MA Support) the user turned off.
        if (on !== defOn && on) {
          pills.push({
            id: filter.enabledKey,
            label: filter.label,
            value: enabledValue,
            displayValue: 'On',
          });
        }
        return;
      }

      // For regular toggles - only show when user turned feature on (same idea as toggle-with-detail pills)
      if (filter.type === 'toggle') {
        const currentValue = filterValues[filter.id];
        const defOn = Boolean(filter.defaultValue);
        const on = coerceFilterBoolean(currentValue, defOn);
        if (on !== defOn && on) {
          pills.push({
            id: filter.id,
            label: filter.label,
            value: currentValue,
            displayValue: 'On',
          });
        }
        return;
      }

      // For toggle-with-detail without enabledKey
      if (filter.type === 'toggle-with-detail' && !filter.enabledKey) {
        const currentValue = filterValues[filter.id];
        const defOn = Boolean(filter.defaultValue);
        const on = coerceFilterBoolean(currentValue, defOn);
        if (on !== defOn && on) {
          pills.push({
            id: filter.id,
            label: filter.label,
            value: currentValue,
            displayValue: 'On',
          });
        }
        return;
      }

      // For other filter types, check if value differs from default
      const currentValue = filterValues[filter.id];
      const defaultValue = filter.defaultValue;

      // Handle arrays (checkbox groups)
      if (Array.isArray(defaultValue) && Array.isArray(currentValue)) {
        const sortedDefault = [...defaultValue].sort();
        const sortedCurrent = [...currentValue].sort();
        if (JSON.stringify(sortedDefault) !== JSON.stringify(sortedCurrent)) {
          pills.push({
            id: filter.id,
            label: filter.label,
            value: currentValue,
            displayValue: formatValueForDisplay(filter.id, currentValue),
          });
        }
        return;
      }

      // toggle-with-detail with children but no enabledKey (rare): mirror enabled-only pill rule
      if (filter.type === 'toggle-with-detail' && filter.children) {
        let hasChange = false;
        filter.children.forEach((child) => {
          const childValue = filterValues[child.id];
          if (childValue !== child.defaultValue) {
            hasChange = true;
          }
        });
        const enabledVal = filter.enabledKey ? filterValues[filter.enabledKey] : filterValues[filter.id];
        const id = filter.enabledKey || filter.id;
        const defOn = Boolean(filter.defaultValue);
        const on = coerceFilterBoolean(enabledVal, defOn);
        const differs = on !== defOn;
        if ((differs && on) || (on && hasChange)) {
          pills.push({
            id,
            label: filter.label,
            value: enabledVal,
            displayValue: formatValueForDisplay(id, enabledVal),
          });
        }
        return;
      }

      // Handle regular values
      if (currentValue !== defaultValue) {
        pills.push({
          id: filter.id,
          label: filter.label,
          value: currentValue,
          displayValue: formatValueForDisplay(filter.id, currentValue),
        });
      }
    });

    return pills;
  }, [filterValues]);

  // Filter config based on search query (excludes uiHidden filters)
  const filteredConfig = useMemo(() => {
    if (!searchQuery.trim()) {
      // Exclude uiHidden filters
      return allFilters.filter((filter) => !filter.uiHidden);
    }

    const query = searchQuery.toLowerCase();
    return allFilters.filter((filter) => {
      // Skip uiHidden filters
      if (filter.uiHidden) return false;
      // Search in label
      if (filter.label.toLowerCase().includes(query)) return true;
      // Search in short description
      if (filter.shortDescription?.toLowerCase().includes(query)) return true;
      // Search in description
      if (filter.description?.toLowerCase().includes(query)) return true;
      // Search in options
      if (filter.options) {
        return filter.options.some(
          (opt) =>
            opt.label.toLowerCase().includes(query) ||
            opt.subtitle?.toLowerCase().includes(query)
        );
      }
      return false;
    });
  }, [searchQuery]);

  // Set value action
  const setValue = useCallback((id: string, value: any) => {
    setFilterValues((prev) => {
      let next = { ...prev, [id]: value };

      // MA filter selection: NONE is exclusive with other MAs (permissive mode)
      if (id === 'ma_filter_selection' && Array.isArray(value)) {
        if (value.includes('NONE')) {
          next[id] = ['NONE'];
        } else {
          next[id] = value.filter((v: string) => v !== 'NONE');
          if (next[id].length === 0) {
            next[id] = ['MA_200'];
          }
        }
      }

      // active_category: apply same pattern presets as old FiltersModal
      // Also sets trend fields for consistency (matching backend TRADING_STYLE_PRESETS)
      if (id === 'active_category' && typeof value === 'string') {
        next.doji_filter_enabled = false;
        next.cup_handle_filter_enabled = false;
        next.head_shoulders_filter_enabled = false;
        next.double_pattern_filter_enabled = false;
        next.breakout_filter_enabled = false;
        next.strong_red_reversal_filter_enabled = false;
        next.green_doji_filter_enabled = false;
        next.pullback_to_support_filter_enabled = false;
        next.fib_golden_zone_filter_enabled = false;
        next.darvas_box_filter_enabled = false;
        // Trend fields (normalized to false on save, but stored for potential future use)
        next.trend_direction = 'any';
        next.trend_strength = 'any';
        next.momentum_confirmation = 'any';
        switch (value) {
          case 'standard_screening':
            next.ma_filter_selection = ['MA_150', 'MA_200'];
            next.ma_proximity_min = -0.02;
            next.ma_proximity_max = 0.045;
            next.bullish_momentum_enabled = false;
            break;
          case 'breakout_specialists':
            next.ma_filter_selection = ['MA_150', 'MA_200'];
            next.ma_proximity_min = -0.02;
            next.ma_proximity_max = 0.045;
            next.bullish_momentum_enabled = false;
            next.breakout_filter_enabled = true;
            break;
          case 'reversal_candidates':
            next.ma_filter_selection = ['MA_150', 'MA_200'];
            next.ma_proximity_min = -0.02;
            next.ma_proximity_max = 0.045;
            next.bullish_momentum_enabled = false;
            next.doji_filter_enabled = true;
            next.head_shoulders_filter_enabled = true;
            break;
          case 'momentum_continuations':
            next.ma_filter_selection = ['MA_150', 'MA_200'];
            next.ma_proximity_min = -0.02;
            next.ma_proximity_max = 0.045;
            // FIX R1: Set to false to match backend TRADING_STYLE_PRESETS
            next.bullish_momentum_enabled = false;
            next.trend_direction = 'any';
            next.cup_handle_filter_enabled = true;
            next.breakout_filter_enabled = true;
            break;
          case 'high_probability':
            next.ma_filter_selection = ['MA_150', 'MA_200'];
            next.ma_proximity_min = -0.02;
            next.ma_proximity_max = 0.045;
            // FIX R1: Set to false to match backend TRADING_STYLE_PRESETS
            next.bullish_momentum_enabled = false;
            next.trend_direction = 'any';
            next.double_pattern_filter_enabled = true;
            next.strong_red_reversal_filter_enabled = true;
            break;
          case 'conservative':
            next.ma_filter_selection = ['MA_150', 'MA_200'];
            next.ma_proximity_min = -0.01;
            next.ma_proximity_max = 0.025;
            next.bullish_momentum_enabled = false;
            break;
          case 'ma_momentum_patterns':
            next.ma_filter_selection = ['MA_150', 'MA_200'];
            next.ma_proximity_min = -0.02;
            next.ma_proximity_max = 0.045;
            next.bullish_momentum_enabled = false;
            next.strong_red_reversal_filter_enabled = true;
            break;
          case 'pullback_to_support':
            next.ma_filter_selection = ['MA_150', 'MA_200'];
            next.ma_proximity_min = -0.015;
            next.ma_proximity_max = 0.03;
            next.bullish_momentum_enabled = false;
            next.pullback_to_support_filter_enabled = true;
            next.green_doji_filter_enabled = true;
            break;
          case 'strong_ma_support':
            next.ma_filter_selection = ['MA_150', 'MA_200'];
            next.ma_proximity_min = -0.01;
            next.ma_proximity_max = 0.02;
            next.bullish_momentum_enabled = false;
            next['strong_ma_support_config.enabled'] = true;
            break;
          case 'ma150_v_reversal_strong_ma':
            next.ma_filter_selection = ['MA_150', 'MA_200'];
            next.ma_proximity_min = -0.02;
            next.ma_proximity_max = 0.045;
            next.bullish_momentum_enabled = false;
            next.ma150_v_reversal_enabled = true;
            next.strong_ma_support_enabled = true;
            // FIX R3: Add nested config to match backend preset
            next['strong_ma_support_config.enabled'] = true;
            break;
          case 'custom':
          default:
            break;
        }
      }

      return next;
    });

    // Auto-expand detail panel when enabling a toggle-with-detail filter
    if (value === true) {
      const filter = allFilters.find((f) => f.enabledKey === id);
      if (filter && filter.detailEnabledKey) {
        setExpandedDetails((prev) => ({
          ...prev,
          [filter.id]: true,
        }));
      }
    }
  }, []);

  // Reset single filter to default
  const resetFilter = useCallback((filterId: string) => {
    // Bug 3 fix: If filterId is an enabledKey, find the owning filter
    let filter = getFilterById(filterId);

    // If no filter found with exact id, check if it's an enabledKey of a toggle-with-detail
    if (!filter) {
      filter = allFilters.find((f) => f.enabledKey === filterId);
    }

    // If still no filter found, return early
    if (!filter) return;

    // Get the filter for use in the state setter - need to capture it since filter is const
    const filterToReset = filter;

    setFilterValues((prev) => {
      const updated = { ...prev };

      // If it's a toggle-with-detail with children
      if (filterToReset.type === 'toggle-with-detail' && filterToReset.children) {
        // Reset the enabled key if exists (use the passed filterId if it's the enabledKey)
        if (filterToReset.enabledKey) {
          updated[filterToReset.enabledKey] = filterToReset.defaultValue ?? false;
        }
        // Reset all children to their defaults
        filterToReset.children.forEach((child) => {
          updated[child.id] = child.defaultValue;
        });
        return updated;
      }

      // Reset to default
      updated[filterId] = filterToReset.defaultValue;
      return updated;
    });
  }, []);

  // Reset all filters to open snapshot (or catalog defaults if no baseline provided)
  const resetAll = useCallback(() => {
    const baseline = getResetBaseline ? getResetBaseline() : {};
    setFilterValues({ ...defaultFilterValues, ...baseline });
    setSearchQuery('');
    setActiveCategory('market');
    setExpandedDetails({});
  }, [getResetBaseline]);

  // Reset all filters to catalog defaults (ignores open snapshot baseline)
  const resetToDefaults = useCallback(() => {
    setFilterValues({ ...defaultFilterValues });
    setSearchQuery('');
    setActiveCategory('market');
    setExpandedDetails({});
  }, []);

  // Toggle detail expansion
  const toggleDetail = useCallback((filterId: string) => {
    setExpandedDetails((prev) => ({
      ...prev,
      [filterId]: !prev[filterId],
    }));
  }, []);

  const setDetailExpanded = useCallback((filterId: string, open: boolean) => {
    setExpandedDetails((prev) => ({
      ...prev,
      [filterId]: open,
    }));
  }, []);

  return {
    // State
    filterValues,
    activeCategory,
    searchQuery,
    activePills,
    filteredConfig,
    expandedDetails,

    // Actions
    setValue,
    setActiveCategory,
    setSearchQuery,
    resetFilter,
    resetAll,
    resetToDefaults,
    toggleDetail,
    setDetailExpanded,
  };
};
